import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import moment from 'moment';
import { Lightbox } from 'react-modal-image';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SendIcon from '@mui/icons-material/Send';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DialogConfirmation from 'src/components/DialogConfarmation';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import MessageBox from 'src/views/management/ChatView/ChatDetails/styled/MessageBox';
import MessageBody from 'src/views/management/ChatView/ChatDetails/styled/MessageBody';
import { deleteChatMessage } from 'src/actions/chatActions';
import { STORAGE, STYLE_OPTIONS } from 'src/constants';
import helpers from 'src/helpers';
import { resolveTransportPendingState } from 'src/helpers/messageTransportPending';
import { getChatMessageSortDate } from 'src/helpers/chatCursor';
import AttachmentRenderer from './AttachmentRenderer';
import TranscriptSection from './TranscriptSection';

const COLOR_GREY_700 = '#8D8D8D';
const COLOR_RED_ERROR = '#F15642';
const FONT_14 = '14px';
const TIMESTAMP_FORMAT_MESSAGE = 'MM/DD/YYYY h:mm a';
const TIMESTAMP_FORMAT_MESSAGE_SHORT = 'h:mm a';
const TIMESTAMP_FORMAT_DELETED_MESSAGE = 'MMM D, h:mm A';

// The time beside a message is when the contact got it: receivedAt for
// inbound, sentAt once an outbound message has gone, scheduledAt while it is
// still queued or failed (CON-1805). The scheduled time is not shown once a
// message has sent - the Sent / Delivered / Read detail is on the tooltip.
const getMessageTimeStamp = (message) => {
  if (message?.isReceived) {
    return message?.receivedAt ? moment(message.receivedAt).format(TIMESTAMP_FORMAT_MESSAGE) : '';
  }
  const displayedAt = message?.sentAt || message?.scheduledAt;
  return displayedAt ? moment(displayedAt).format(TIMESTAMP_FORMAT_MESSAGE) : '';
};

// Sent at / Delivered at / Read at, for a message that has gone. Rendered as
// one tooltip over the whole timestamp line, not only the tick. Null while
// the message is queued, unconfirmed or failed - those states carry their
// own caption and tooltip from getMessageStatusIcon.
const getMessageStatusTooltip = (message) => {
  const statusObj = helpers.transform.jsonToObject(message?.status);
  const isSendingError = !!statusObj?.error;

  if (message?.isReceived || helpers.isEmpty(message?.sentAt) || isSendingError) {
    return null;
  }

  // Short form on the same day as the time printed beside the message.
  const displayedAt = getChatMessageSortDate(message);
  const getFormattedStatusTimestamp = (timestamp) => (
    moment(timestamp).isSame(displayedAt, 'day')
      ? moment(timestamp).format(TIMESTAMP_FORMAT_MESSAGE_SHORT)
      : moment(timestamp).format(TIMESTAMP_FORMAT_MESSAGE));

  const sentAt = getFormattedStatusTimestamp(message.sentAt);
  const deliveredAt = message?.deliveredAt ? getFormattedStatusTimestamp(message.deliveredAt) : 'Not Delivered Yet';
  const readAt = message?.readAt ? getFormattedStatusTimestamp(message.readAt) : 'Not Read Yet';

  if (!helpers.isEmpty(message?.readAt)) {
    return (
      <>
        {`Sent at: ${sentAt}`}
        <br />
        {`Delivered at: ${deliveredAt}`}
        <br />
        {`Read at: ${readAt}`}
      </>
    );
  }

  if (!helpers.isEmpty(message?.deliveredAt)) {
    return (
      <>
        {`Sent at: ${sentAt}`}
        <br />
        {`Delivered at: ${deliveredAt}`}
      </>
    );
  }

  return `Sent at: ${sentAt}`;
};

const getMessageStatusIcon = (message) => {
  const style = {
    fontSize: FONT_14,
    color: COLOR_GREY_700
  };

  const statusObj = helpers.transform.jsonToObject(message?.status);
  const isSendingError = !!statusObj?.error;

  // The three sent states: the tick alone. Their tooltip is the line-level
  // one from getMessageStatusTooltip, so hovering anywhere on the timestamp
  // line opens it.
  if (!helpers.isEmpty(message?.readAt)) {
    return (
      <DoneAllIcon
        color="secondary"
        sx={{ fontSize: FONT_14 }}
      />
    );
  }

  if (!helpers.isEmpty(message?.deliveredAt)) {
    return <DoneAllIcon sx={style} />;
  }

  if (!helpers.isEmpty(message?.sentAt) && !isSendingError) {
    return <CheckIcon sx={style} />;
  }

  // Transport-unconfirmed (Cloudflare 504 / origin 5xx / send timeout). The
  // send most likely succeeded and only the response was lost, so inside the
  // pending window this renders NEUTRALLY — grey caption, no error icon,
  // visually a sent-but-unconfirmed message rather than a failure. Once the
  // window elapses with no confirmation it falls through to the error
  // treatment below using the backend-supplied terminal string.
  const transportPending = resolveTransportPendingState(message, statusObj);

  if (transportPending.isPending) {
    return (
      <TooltipMUI6 title="The carrier has not confirmed this message yet. This usually resolves on its own.">
        <Typography
          variant="caption"
          color={COLOR_GREY_700}
        >
          {transportPending.text}
        </Typography>
      </TooltipMUI6>
    );
  }

  if (isSendingError || transportPending.isExpired) {
    // CON-1679: this sits INLINE on the timestamp line, right-aligned, and is
    // expected to be one line. `error` is the short label written for exactly
    // that (one clause, fits beside a timestamp); `errorLong` is two or three
    // sentences and would wrap the line several times and push the layout
    // around, so it goes on hover instead.
    const errorText = transportPending.isExpired
      ? transportPending.text
      : statusObj.error;

    const errorCaption = (
      <>
        <Typography
          variant="caption"
          color={COLOR_RED_ERROR}
        >
          {errorText}
        </Typography>
        <ErrorIcon
          sx={{
            fontSize: FONT_14,
            color: COLOR_RED_ERROR,
          }}
        />
      </>
    );

    // No tooltip rather than an empty one when there is no explanation to
    // give: every row written before CON-1679 has only `error`, and the
    // transport-expired string is already self-explanatory.
    const explanation = transportPending.isExpired ? null : statusObj.errorLong;

    if (!explanation) {
      return errorCaption;
    }

    return (
      <TooltipMUI6 title={explanation}>
        <span>{errorCaption}</span>
      </TooltipMUI6>
    );
  }

  return null;
};

const getDeleteConfirmationNote = (message) => {
  if (message?.isReceived) {
    return 'it will not be deleted from the sender`s connection history.';
  }

  if (!helpers.isEmpty(message?.sentAt)) {
    return 'it will not be deleted from the recipient`s connection history.';
  }

  return '';
};

function Message({
  message,
  showPhoneNumber,
  userCanDeleteTexts,
  canViewTestDeliverability = false,
  onTestDeliverability = () => {},
  canReportNotDelivered = false,
  onReportNotDelivered = () => {},
  lookItUpState = 'hidden',
  onLookItUp = () => {},
}) {
  const [openedFile, setOpenedFile] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [kebabAnchorEl, setKebabAnchorEl] = useState(null);
  const { user, azAccountName, databaseBeingUsed } = useSelector((state) => state?.account || {});
  const { chatKey } = useParams();
  const dispatch = useDispatch();

  const isUserMessage = !message?.isReceived;
  const isMessageDeleted = !!message?.deletedAt;
  const statusTooltip = isMessageDeleted ? null : getMessageStatusTooltip(message);

  const handleContextMenu = (event) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
        }
        : null,
    );
  };

  const handleClose = () => {
    setContextMenu(null);
    setKebabAnchorEl(null);
  };

  const handleKebabClick = (event) => {
    setKebabAnchorEl(event.currentTarget);
  };

  const handleTestDeliverabilityClick = () => {
    handleClose();
    onTestDeliverability(message);
  };

  const handleLookItUpClick = () => {
    handleClose();
    onLookItUp(message);
  };

  const handleReportNotDeliveredClick = () => {
    handleClose();
    onReportNotDelivered(message);
  };

  const handleDeleteMessage = () => {
    dispatch(deleteChatMessage(chatKey, message.id));
    handleClose();
  };

  // const handleCopyText = async () => {
  //   try {
  //     const textToCopy = message?.body?.trim() || '';
  //     if (textToCopy) {
  //       await navigator.clipboard.writeText(textToCopy);
  //     }
  //   } catch (error) {
  //     // Fallback for older browsers
  //     try {
  //       const textArea = document.createElement('textarea');
  //       textArea.value = message?.body?.trim() || '';
  //       document.body.appendChild(textArea);
  //       textArea.select();
  //       document.execCommand('copy');
  //       document.body.removeChild(textArea);
  //     } catch (fallbackError) {
  //       console.error('Fallback copy failed:', fallbackError);
  //     }
  //   }
  //   handleClose();
  // };

  // Check if message has copyable text
  const hasTextToCopy = !isMessageDeleted && message?.body?.trim();
  const menuOpen = contextMenu !== null || Boolean(kebabAnchorEl);
  const showTestDeliverability = isUserMessage && canViewTestDeliverability && !isMessageDeleted;
  // Mirrors messageDeliveryReportService.createReport: sent, outbound, not
  // deleted. A carrier-refused message is reportable on purpose; that refusal
  // is the signal the report collects.
  // CON-2052. Support account only: lookItUpState is 'hidden' everywhere
  // else, and on a chat with no current draft, like the chip.
  const showLookItUp = !isUserMessage && !isMessageDeleted && lookItUpState !== 'hidden';
  const showReportNotDelivered = isUserMessage
    && canReportNotDelivered
    && !isMessageDeleted
    && !!message?.sentAt;

  return (
    <>
      <MessageBox isUser={isUserMessage}>
        <Box>
          {showPhoneNumber && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '12px',
                  lineHeight: '14px',
                }}
              >
                {isUserMessage ? 'Sent to:' : 'Received from: '}
              </Typography>
              <Chip
                size="small"
                icon={<LocalPhoneOutlinedIcon />}
                label={helpers.transform.toPhoneNumber(message?.phone || 'Unknown number')}
                sx={{
                  padding: '3px',
                  fontSize: '12px',
                  backgroundColor: '#F7F8FB',
                }}
              />
            </Box>
          )}
          <div
            onContextMenu={(event) => {
              event.preventDefault();
              if (!isMessageDeleted && (userCanDeleteTexts || hasTextToCopy)) {
                handleContextMenu(event);
              }
            }}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              const timer = setTimeout(() => {
                if (!isMessageDeleted && (userCanDeleteTexts || hasTextToCopy)) {
                  // Prevent iOS text selection magnifier
                  e.preventDefault();
                  handleContextMenu({
                    preventDefault: () => { },
                    clientX: touch.clientX,
                    clientY: touch.clientY
                  });
                }
              }, 500);
              e.currentTarget.longPressTimer = timer;
            }}
            onTouchEnd={(e) => {
              if (e.currentTarget.longPressTimer) {
                clearTimeout(e.currentTarget.longPressTimer);
              }
            }}
            onTouchMove={(e) => {
              if (e.currentTarget.longPressTimer) {
                clearTimeout(e.currentTarget.longPressTimer);
              }
            }}
            style={{
              cursor: 'context-menu',
              WebkitUserSelect: 'none',
              userSelect: 'none',
            }}
          >
            <MessageBody
              isUser={isUserMessage}
            >
              <Box
                sx={{
                  padding: isUserMessage ? '8px 8px 8px 16px' : '8px 16px 8px 8px',
                }}
              >
                {/* Render stored attachments */}
                {message?.attachments?.length > 0 && !isMessageDeleted && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: (message.attachments.length + (message?.externalAttachments?.length || 0)) === 1 ? 'nowrap' : 'wrap',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    {message.attachments.filter((attachment) => !!attachment)
                      .map((fileName) => {
                        const url = helpers.azure.getImageFileURL(
                          fileName,
                          user?.id,
                          azAccountName,
                          STORAGE.sas,
                          databaseBeingUsed,
                          message.messageAttachmentContainer,
                          true
                        );
                        const totalAttachments = message.attachments.length + (message?.externalAttachments?.length || 0);
                        const isSingle = totalAttachments === 1;

                        // CON-1674 — a browser-playable copy of an attachment
                        // no browser decodes (today: AMR). The server records
                        // the derived blob name only after a successful
                        // upload, so an entry here means the file exists;
                        // no entry means the renderer falls back to the
                        // download-only card. The name is never derived
                        // client-side, precisely so that absence is
                        // meaningful rather than a guess that 404s.
                        const playback = message?.playbackAudio?.find(
                          (entry) => entry?.source === fileName
                        );
                        const playbackUrl = playback?.file
                          ? helpers.azure.getImageFileURL(
                            playback.file,
                            user?.id,
                            azAccountName,
                            STORAGE.sas,
                            databaseBeingUsed,
                            message.messageAttachmentContainer,
                            true
                          )
                          : '';

                        return (
                          <AttachmentRenderer
                            key={`stored-${fileName}-${message.id}`}
                            fileName={fileName}
                            url={url}
                            playbackUrl={playbackUrl}
                            isSingle={isSingle}
                            onPreview={setOpenedFile}
                            isExternal={false}
                          />
                        );
                      })}
                  </Box>
                )}
                {/* Render external attachments */}
                {message?.externalAttachments?.length > 0 && !isMessageDeleted && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: ((message?.attachments?.length || 0) + message.externalAttachments.length) === 1 ? 'nowrap' : 'wrap',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    {message.externalAttachments.map((extFile) => {
                      const totalAttachments = (message?.attachments?.length || 0) + message.externalAttachments.length;
                      const isSingle = totalAttachments === 1;

                      // Check if link is expiring soon (within 6 hours)
                      const isExpiringSoon = moment().add(6, 'hours').isAfter(moment(extFile.expires));

                      // Create unique key using fileName and a hash of the URL
                      const urlHash = extFile.url.slice(-10);

                      return (
                        <AttachmentRenderer
                          key={`ext-${extFile.fileName}-${urlHash}-${message.id}`}
                          fileName={extFile.fileName}
                          url={extFile.url}
                          isSingle={isSingle}
                          onPreview={setOpenedFile}
                          isExternal
                          fileSize={extFile.size}
                          contentType={extFile.contentType}
                          expiryWarning={isExpiringSoon}
                        />
                      );
                    })}
                  </Box>
                )}
                {isMessageDeleted ? (
                  <TooltipMUI6
                    title={(
                      <>
                        <Box>
                          <Typography variant="h5">Deleted message </Typography>
                          <Typography
                            variant="caption"
                            color={COLOR_GREY_700}
                          >
                            {moment(message.deletedAt).format(TIMESTAMP_FORMAT_DELETED_MESSAGE)}
                          </Typography>
                        </Box>
                        {isUserMessage && (
                          <Box
                            sx={{ marginTop: '10px' }}
                          >
                            <Typography variant="body2">
                              {message?.sentAt ? `- Sent at: ${moment(message.sentAt).format(TIMESTAMP_FORMAT_DELETED_MESSAGE)}` : '- Not Sent Yet'}
                            </Typography>
                            <Typography variant="body2">
                              {message?.deliveredAt ? `- Received at: ${moment(message.deliveredAt).format(TIMESTAMP_FORMAT_DELETED_MESSAGE)}` : '- Not Received Yet'}
                            </Typography>
                            <Typography variant="body2">
                              {message?.readAt ? `- Read at: ${moment(message.readAt).format(TIMESTAMP_FORMAT_DELETED_MESSAGE)}` : '- Not Read Yet'}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '4px',
                        color: COLOR_GREY_700,
                      }}
                    >
                      <DeleteForeverIcon />
                      <Typography
                        variant="body1"
                        fontStyle="italic"
                      >
                        {`This message was deleted${(message?.sentAt || message?.receivedAt) ? ' only for you' : ''}`}
                      </Typography>
                    </Box>
                  </TooltipMUI6>
                ) : (
                  <Typography
                    color={!message?.body?.trim() ? COLOR_GREY_700 : 'inherit'}
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word',
                      fontStyle: !message?.body?.trim() ? 'italic' : 'normal',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {(helpers.isEmpty(message?.attachments?.filter((attachment) => !!attachment)) && !message?.body?.trim()) ? 'Empty Message' : helpers.linkifyText(message?.body)}
                  </Typography>
                )}
                {/* CON-1674: voice memo transcript, after the attachment and
                    after any sender-typed text, never merged into the body */}
                {!isMessageDeleted && (
                  <TranscriptSection transcription={message?.transcription} />
                )}
              </Box>
            </MessageBody>
            <Menu
              open={menuOpen}
              onClose={handleClose}
              {...(contextMenu !== null
                ? {
                  anchorReference: 'anchorPosition',
                  anchorPosition: {
                    top: contextMenu.mouseY,
                    left: contextMenu.mouseX,
                  },
                }
                : {
                  anchorEl: kebabAnchorEl,
                }
              )}
            >
              {showLookItUp && (
                <MenuItem
                  onClick={handleLookItUpClick}
                  disabled={lookItUpState === 'pending'}
                  title="Look up this consultant's account and research this message"
                >
                  <ManageSearchIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
                  {lookItUpState === 'pending' ? 'Looking it up...' : 'Look it up'}
                </MenuItem>
              )}
              {hasTextToCopy && (
                <MenuItem
                  onClick={async () => {
                    const textToCopy = message?.body?.trim() || '';
                    if (textToCopy) {
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                          handleClose();
                          return;
                        } catch (err) {
                          // Fall through to execCommand
                        }
                      }
                      const textarea = document.createElement('textarea');
                      textarea.value = textToCopy;
                      textarea.setAttribute('readonly', '');
                      textarea.style.position = 'absolute';
                      textarea.style.left = '-9999px';
                      document.body.appendChild(textarea);
                      if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
                        const range = document.createRange();
                        range.selectNodeContents(textarea);
                        const selection = window.getSelection();
                        selection.removeAllRanges();
                        selection.addRange(range);
                        textarea.setSelectionRange(0, 999999);
                      } else {
                        textarea.focus();
                        textarea.select();
                      }
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                    }
                    handleClose();
                  }}
                >
                  <ContentCopyIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
                  Copy text
                </MenuItem>
              )}
              {showTestDeliverability && (
                <MenuItem onClick={handleTestDeliverabilityClick}>
                  <SendIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
                  Test Deliverability
                </MenuItem>
              )}
              {showReportNotDelivered && (
                <MenuItem onClick={handleReportNotDeliveredClick}>
                  <ReportProblemOutlinedIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
                  Report Not Delivered
                </MenuItem>
              )}
              {userCanDeleteTexts && (
                <DialogConfirmation
                  header="Delete Message"
                  title="Are you sure you want to delete this message?"
                  note={getDeleteConfirmationNote(message)}
                  okText="Delete"
                  icon={<DeleteOutlineIcon htmlColor={STYLE_OPTIONS.COLOR_RED} />}
                  iconType="warning"
                  onConfirm={handleDeleteMessage}
                  onCancel={handleClose}
                >
                  <MenuItem>
                    <DeleteOutlineIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
                    Delete message
                  </MenuItem>
                </DialogConfirmation>
              )}
            </Menu>
          </div>
          <Box
            sx={{
              display: 'flex',
              justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
              alignItems: 'center',
              gap: '4px',
              mt: '8px',
            }}
          >
            {isUserMessage && !isMessageDeleted && (
              <IconButton
                className="kebab-icon"
                size="small"
                onClick={handleKebabClick}
                sx={{
                  padding: '2px',
                  color: COLOR_GREY_700,
                }}
              >
                <MoreHorizIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            )}
            {statusTooltip ? (
              <TooltipMUI6 title={statusTooltip}>
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}
                >
                  <Typography
                    color={COLOR_GREY_700}
                    variant="caption"
                  >
                    {getMessageTimeStamp(message)}
                  </Typography>
                  {getMessageStatusIcon(message)}
                </Box>
              </TooltipMUI6>
            ) : (
              <>
                <Typography
                  color={COLOR_GREY_700}
                  variant="caption"
                >
                  {getMessageTimeStamp(message)}
                </Typography>
                {!message?.isReceived && !message?.deletedAt && getMessageStatusIcon(message)}
              </>
            )}
            {!isUserMessage && !isMessageDeleted && (
              <IconButton
                className="kebab-icon"
                size="small"
                onClick={handleKebabClick}
                sx={{
                  padding: '2px',
                  color: COLOR_GREY_700,
                }}
              >
                <MoreHorizIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </MessageBox>
      {openedFile && (
        <Lightbox
          large={openedFile}
          onClose={() => setOpenedFile(null)}
          hideDownload
          showRotate
        />
      )}
    </>
  );
}

Message.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    body: PropTypes.string,
    attachments: PropTypes.arrayOf(PropTypes.string), // Stored file names
    externalAttachments: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string.isRequired,
      fileName: PropTypes.string.isRequired,
      size: PropTypes.number,
      contentType: PropTypes.string,
      expires: PropTypes.string
    })), // External file links
    isReceived: PropTypes.bool,
    scheduledAt: PropTypes.string,
    receivedAt: PropTypes.string,
    sentAt: PropTypes.string,
    deliveredAt: PropTypes.string,
    readAt: PropTypes.string,
    deletedAt: PropTypes.string,
    messageId: PropTypes.string,
    status: PropTypes.string,
    phone: PropTypes.string,
    messageAttachmentContainer: PropTypes.string,
    transcription: PropTypes.string, // CON-1674: voice memo transcript
    // CON-1674: browser-playable copies of attachments no browser decodes.
    // `source` matches an entry in `attachments`; `file` is a derived blob in
    // the same container.
    playbackAudio: PropTypes.arrayOf(PropTypes.shape({
      source: PropTypes.string,
      file: PropTypes.string
    }))
  }).isRequired,
  showPhoneNumber: PropTypes.bool,
  userCanDeleteTexts: PropTypes.bool,
  canViewTestDeliverability: PropTypes.bool,
  onTestDeliverability: PropTypes.func,
  canReportNotDelivered: PropTypes.bool,
  onReportNotDelivered: PropTypes.func,
  // CON-2052: 'hidden' | 'ready' | 'pending', from the compose box's AiLookup
  lookItUpState: PropTypes.oneOf(['hidden', 'ready', 'pending']),
  onLookItUp: PropTypes.func,
};

Message.defaultProps = {
  showPhoneNumber: false,
  userCanDeleteTexts: false,
  canViewTestDeliverability: false,
  onTestDeliverability: () => {},
  canReportNotDelivered: false,
  onReportNotDelivered: () => {},
};

export default Message;
